import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Linking,
  ScrollView,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { MenuItem, MenuProps } from './types';
import { menuStyled as styled } from './styled';
import { Footer } from '../../components/Footer';
import MenuCollapse from './components/MenuCollapse';
import { TextInput } from '../../components/TextInput/TextInput';
import { STYLE_VARS } from '../../constants/style-vars';
import { Image } from '../../components/Image';
import { Button } from '../../components/Button';
import { menuItems } from './constants';
import { wooCommerceApi } from '../../services/api/wooCommerceApi';
import { useDispatch, useSelector } from 'react-redux';
import { COLORS, FONTS } from '../../constants';
import { upperCase } from 'lodash';
import { NavigationService } from '../../services/navigation';
import { Alert } from '../../services/alert';
import {
  logout,
  setUnreadMessage,
} from '../../services/redux/reducers/AuthSlice';
import { RootState } from '../../services/redux';
import ChatRoomApi from '../../services/api/chatroom.api';
import { useRole } from '../../hooks/useRole';

const Menu: FC<MenuProps> = ({ navigation }) => {
  const { isVolunteerIntructor } = useRole();
  const dispatch = useDispatch();
  const { user, unreadMessage } = useSelector(
    (redux: RootState) => redux.authReducer
  );
  const { height: screenHeight } = useWindowDimensions();

  const getUnreadMessage = useCallback(async () => {
    ChatRoomApi.getUnreadMessage().then((res) => {
      dispatch(setUnreadMessage(res.data.data.unread_messages));
    });
  }, [dispatch]);

  const handleLogout = useCallback(() => {
    Alert.alert('Logout', 'You want to logout?', [
      {
        text: 'Ok',
        onPress: () => {
          dispatch(logout());
          NavigationService.push('Auth');
        },
      },
      {
        text: 'Cancel',
        onPress: () => {},
      },
    ]);
  }, [dispatch]);

  const menuList = useMemo(() => {
    if (isVolunteerIntructor) {
      return [
        {
          icon: require('../../assets/mountain.png'),
          title: 'Book Activities',
          onPress: () => {},
        },
        {
          icon: require('../../assets/booked.png'),
          title: 'Upcoming Bookings',
          onPress: () => {
            NavigationService.push<'Root'>('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'MyBookings',
              },
            });
          },
        },
        {
          icon: require('../../assets/user-circle.png'),
          title: 'My Account',
          onPress: () => {
            NavigationService.push('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'MyProfile',
              },
            });
          },
        },
        {
          icon: require('../../assets/letter-round.png'),
          title: 'Contact Us',
          onPress: () => {
            NavigationService.push<'Root'>('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'ContactUs',
              },
            });
          },
        },
        {
          icon: require('../../assets/logout-icon.png'),
          title: 'Log Out',
          onPress: () => {
            handleLogout();
          },
        },
      ];
    } else {
      return [
        {
          icon: require('../../assets/messages-1.png'),
          title: 'Messages',
          onPress: () => {
            NavigationService.push('Root', {
              screen: 'Messages',
            });
          },
          rightItem:
            unreadMessage && unreadMessage > 0 ? (
              <View
                style={{
                  width: 24,
                  height: 24,
                  backgroundColor: COLORS.RED_PRIMARY,
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 24,
                }}
              >
                <Text
                  style={{
                    color: COLORS.WHITE,
                    fontFamily: FONTS.AvenirMedium,
                    fontSize: 14,
                  }}
                >
                  {unreadMessage}
                </Text>
              </View>
            ) : (
              <></>
            ),
        },
        {
          icon: require('../../assets/booked.png'),
          title: 'Make A Booking',
          onPress: () => {
            NavigationService.push('Root', {
              screen: 'HomeTab',
            });
          },
        },
        {
          icon: require('../../assets/messages-icon.png'),
          title: 'Camps',
          onPress: () => {
            NavigationService.push('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'BookingList',
              },
            });
          },
        },
        {
          icon: require('../../assets/booked.png'),
          title: 'My Bookings',
          onPress: () => {
            NavigationService.push<'Root'>('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'MyBookings',
              },
            });
          },
        },
        {
          icon: require('../../assets/mountain.png'),
          title: 'My Activities',
          onPress: () => {
            NavigationService.push<'Root'>('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'MyActivities',
              },
            });
          },
        },
        {
          icon: require('../../assets/notice-icon.png'),
          title: 'Notices',
          onPress: () => {
            NavigationService.push<'Root'>('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'Notices',
              },
            });
          },
        },
        {
          icon: require('../../assets/user-circle.png'),
          title: 'My Account',
          onPress: () => {
            NavigationService.push('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'MyProfile',
              },
            });
          },
        },
        {
          icon: require('../../assets/letter-round.png'),
          title: 'Contact Us',
          onPress: () => {
            NavigationService.push<'Root'>('Root', {
              screen: 'MenuTab',
              params: {
                screen: 'ContactUs',
              },
            });
          },
        },
        {
          icon: require('../../assets/logout-icon.png'),
          title: 'Log Out',
          onPress: () => {
            handleLogout();
          },
        },
      ];
    }
  }, [handleLogout, unreadMessage, isVolunteerIntructor]);

  const renderMyAccountButtons = useCallback(() => {
    return (
      <View
        style={{
          flexDirection: 'column',
          paddingLeft: 20,
        }}
      >
        {menuList.map((item, index) => {
          return (
            <TouchableOpacity
              onPress={item.onPress}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 8,
                justifyContent: 'space-between',
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Image
                  source={item.icon}
                  style={{
                    width: 24,
                    height: 24,
                    marginRight: 15,
                    tintColor: '#505050',
                    resizeMode: 'contain',
                  }}
                />
                <Text
                  style={{
                    fontFamily: FONTS.AvenirMedium,
                    fontWeight: '500',
                    fontSize: 20,
                    color: COLORS.GREY,
                    letterSpacing: 0.5,
                  }}
                >
                  {item.title}
                </Text>
              </View>
              {item?.rightItem}
            </TouchableOpacity>
          );
        })}
      </View>
    );
  }, [menuList]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      getUnreadMessage();
    });

    return unsubscribe;
  }, [getUnreadMessage, navigation]);

  return (
    <ScrollView>
      <View
        style={{
          flexDirection: 'row',
          paddingVertical: 20,
          paddingHorizontal: 20,
          justifyContent: 'space-between',
          elevation: 4,
          shadowColor: '#0C0C0D1A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 1,
          shadowRadius: 4,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.AvenirMedium,
            fontWeight: '500',
            fontSize: 20,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.Arial,
              fontWeight: '700',
              fontSize: 20,
              textTransform: 'uppercase',
              color: COLORS.BLACK,
            }}
          >
            WELCOME,{' '}
          </Text>
          <Text
            style={{
              fontFamily: FONTS.Arial,
              fontSize: 20,
              textTransform: 'uppercase',
              color: COLORS.BLACK,
            }}
          >{`${user?.first_name} ${user?.last_name}`}</Text>
        </Text>
      </View>
      <View
        style={{
          paddingHorizontal: 40,
          paddingVertical: 40,
          backgroundColor: COLORS.LIGHT_RED,
          height: screenHeight,
        }}
      >
        <View
          style={{
            paddingBottom: 15,
            borderBottomWidth: 1,
            borderBottomColor: COLORS.BLACK,
            marginBottom: 10,
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.Arial,
              fontWeight: '700',
              fontSize: 20,
              color: COLORS.BLACK,
              textTransform: 'uppercase',
            }}
          >
            MENU
          </Text>
          <TouchableOpacity
            onPress={() => {
              handleLogout();
            }}
          >
            <Image
              source={require('../../assets/logout-icon.png')}
              style={{ width: 24, height: 24 }}
            />
          </TouchableOpacity>
        </View>
        {renderMyAccountButtons()}
      </View>
      <Footer />
    </ScrollView>
  );
};
export default Menu;
